import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Persisted view-mode preference for the Finance ledger lists (CLAUDE.md §2.3),
 * mirroring the CRM/HRM list stores. Search filters live in URL-bound page
 * state and stay out of the store — those are server-driven query params, not
 * durable user prefs. Ordering stays ephemeral so refreshes return to a clean
 * default.
 */
export type LedgerViewMode = 'table' | 'list' | 'cards' | 'grouped';

export const LEDGER_VIEW_MODES: LedgerViewMode[] = ['table', 'list', 'cards', 'grouped'];

interface LedgerUiState {
  view: LedgerViewMode;
  setView: (view: LedgerViewMode) => void;
  /** Current DRF `OrderingFilter` value (e.g. `-date`). Session-only. */
  ordering: string | undefined;
  setOrdering: (ordering: string | undefined) => void;
}

/** Small factory so Income and Expense share one identical store shape. */
function createLedgerUiStore(persistName: string) {
  return create<LedgerUiState>()(
    persist(
      (set) => ({
        view: 'table',
        setView: (view) => set({ view }),
        ordering: undefined,
        setOrdering: (ordering) => set({ ordering }),
      }),
      {
        name: persistName,
        // Only the view mode is durable.
        partialize: (state) => ({ view: state.view }),
      },
    ),
  );
}

export const useIncomeUi = createLedgerUiStore('finance.income:ui');
export const useExpenseUi = createLedgerUiStore('finance.expense:ui');
